/*
 *   main page that renders level 1 challenges for users to attempt
 *   including code reveal
 */

import Styles from './ChallengeLevelOne.module.scss';
// React Imports.
import { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

// asset imports
import _ from 'lodash';
import axios from "axios";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Box, TextField, FormHelperText, Backdrop, Typography, Paper, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

// Components import.
import { useAPI, AuthContext } from 'components/lib';
import { ChallengeInfo } from 'components/global/ChallengeLayout/ChallengeInfo';
import { ChallengeInfoFloat } from 'components/global/ChallengeLayout/ChallengeInfoFloat';
import { TestCaseTimeline } from 'components/global/ChallengeLayout/TestCaseTimeline';
import { CompleteCard } from 'components/global/ChallengeLayout/CompleteCard';
import { Hints } from 'components/global/ChallengeLayout/Hints';
import { ChallengeExplanation } from 'components/global/ChallengeLayout/ChallengeExplanation';
import { ConfirmationCard } from 'components/global/ConfirmationCard/ConfirmationCard';
import { LoadingBackdrop } from 'components/global/LoadingBackdrop/LoadingBackdrop';
import { Joyride } from 'components/walkthrough/walkthrough';
import walkthroughJSON from 'utils/constants/instructions.json';

// asset imports
import failedAttemptSound from 'assets/sounds/try-again.mp3';

export function ChallengeLevelOne(props) {
	const { challenge_id } = useParams();
	const { stream_id } = useParams();
	const context = useContext(AuthContext);
	// TODO: * useStates can be combined into a map
	const [challengeData, setChallengeData] = useState([]);
	// code reveal data
	const [codeReveal, setCodeReveal] = useState({
		codeRevealText: props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.codeRevel.text'),
		codeRevealMessage:
			props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.codeRevel.message'),
	});

	//keep track of the current status
	const [currentTracker, setCurrentTracker] = useState({
		attemptTrack: 1,
		attemptExceed: false,
		testUncover: [],
		currCoverage: 0
	})

	const [showWalk, setShowWalk] = useState(0)

	// stepData for timeline
	const [stepData, setStepData] = useState(
		[["abcd", "Negative"], ["Already Uncovered: abcd", "Negative"]]
		// props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.dummyStep', { returnObjects: true }), 
		);
	// tracking if there has been a change in input or not
	const [repeatedInput, setRepeatedInput] = useState(false);
	// flag to check challenge is completed
	const [challengeCompleted, setChallengeCompleted] = useState(false);
	// flag to check minPercentToast comes only once
	const [minCoverageNotified, setminCoverageNotified] = useState(false);
	// flag for focusing on code reveal text
	const [focusOnCodeReveal, setFocusOnCodeReveal] = useState(false);
	// flag for info pop up
	const [showInfo, setShowInfo] = useState(true);

	const wastedAttemptAudio = new Audio(failedAttemptSound);

	const [attemptsShake, setAttemptsShake] = useState(false);

	// summary for complete card
	const [userChallengeData, setUserChallengeData] = useState({
		challenge_score: 0,
		time_taken: '',
		numTestUncovered: 0,
		hints: 0,
		score: null,
	});
	const [hintText, setHintText] = useState({
		hint1: '',
		hint2: '',
	});

	const [showCompleteConfirmCard, setShowCompleteConfirmCard] = useState(false);

	const { data, loading } = useAPI(`/api/streams/${stream_id}/challenges/${challenge_id}`, 'GET');
	const navigate = useNavigate();

	const [inputs, setInputs] = useState([]);

	const unfocusOnCodeReveal = () => {
		setFocusOnCodeReveal(false);
	};

	const showChallengeInfo = () => {
		setShowInfo(true);
	};

	const hideChallengeInfo = () => {
		setShowInfo(false);
		setShowWalk(showWalk + 1)
	};

	// warn when user reloads & close the tab
	useEffect(() => {
		const handleBeforeUnload = (event) => {
			event.preventDefault();
			const message = props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.warn.reload');
			event.returnValue = message;
			return message;
		};

		const handlePageLeave = () => {
			return props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.warn.close');
		};

		window.addEventListener('beforeunload', handleBeforeUnload);
		window.addEventListener('unload', handlePageLeave);

		return () => {
			window.removeEventListener('beforeunload', handleBeforeUnload);
			window.removeEventListener('unload', handlePageLeave);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	useEffect(() => {
		if (data?.length) {
			setChallengeData(data);
			const initialUncovered = {};
			data[0].testCases.forEach(testCase => {
				initialUncovered[testCase] = -1;
			});
			setCurrentTracker(prev => ({
				...prev,
				testUncover: initialUncovered
			}))
			document.title = props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.title',{name:data[0].name});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	const handleInputChange = (e, index) => {
		const newInputs = [...inputs];
		newInputs[index] = e.target.value;
		setInputs(newInputs);
		setRepeatedInput(false);
	};

	const addStepData = (testCaseText, caseSign = "none") => {
		if (testCaseText !== '') {
			setStepData(prevState => [...prevState, [testCaseText, _.startCase(_.toLower(caseSign))]]);
		}
	}

	const errorToast = (message) => {
		toast.dark(message, {
			type: 'error',
			position: 'top-right',
			autoClose: false,
			hideProgressBar: false,
			closeOnClick: true,
			pauseOnHover: true,
			draggable: true,
			progress: undefined,
		});
	};

	const handleValidate = (input_list) => {
		setRepeatedInput(true);
		if (currentTracker.attemptTrack <= challengeData[0].attempts) {
			const modifiedList = input_list.map(str => str.replace(/~/g, "'"));
			const csvString = modifiedList.join('~~~');
			const replacedString = csvString.replace(/"/g, "'");
			const options = {
				method: 'POST',
				url: `/api/streams/${stream_id}/challenges/${challenge_id}/validate`,
				data: { input_list: replacedString },
				headers: {
					accept: 'application/json',
				},
			};
			axios
				.request(options)
				.then(function (response) {
					// seperate testCaseId, testCaseMessage, codeRevealString from the code_reveal api
					const detailArray = response.data.data.split('|||');
					const [testCaseId, testCaseMessage, codeRevealString, codeMessage, testCaseType] = detailArray;
					const titleCaseConvertedString = _.startCase(_.toLower(testCaseMessage));
					setCodeReveal({
						codeRevealText: codeRevealString,
						codeRevealMessage: codeMessage,
					});
					// checks if test case is already uncovered
					if (currentTracker.testUncover[testCaseId] === -1) {
						document.documentElement.scrollTo({
							top: document.documentElement.scrollHeight,
							behavior: 'smooth',
						});
						setFocusOnCodeReveal(true);
						addStepData(titleCaseConvertedString, testCaseType);
						setCurrentTracker(prevState => ({
							...prevState,
							currCoverage: prevState.currCoverage + 1,
							testUncover: {
								...prevState.testUncover,
								[testCaseId]: csvString,
							}
						}));
						setUserChallengeData((prevState) => ({
							...prevState,
							numTestUncovered: prevState.numTestUncovered + 1,
						}));
						if (!minCoverageNotified) {
							// toast after completing min % test coverage
							if (
								Math.ceil(((currentTracker.currCoverage + 1) / challengeData[0].numOfTestCases) * 100) >=
								challengeData[0].minPercent
							) {
								toast.dark(props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.toast.minPercentage'), {
									position: 'bottom-right',
									autoClose: false,
									hideProgressBar: false,
									// closeOnClick: true,
									pauseOnHover: true,
									draggable: true,
									progress: undefined,
								});
								setminCoverageNotified(true);
							}
						}

						// toast after completing 100% test coverage
						if (challengeData[0].numOfTestCases - 1 === currentTracker.currCoverage) {
							setCurrentTracker(prev => ({
								...prev,
								attemptExceed: true
							}));
							toast.dark(props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.toast.completed'), {
								type: 'success',
								position: 'bottom-right',
								autoClose: false,
								hideProgressBar: false,
								// closeOnClick: true,
								pauseOnHover: true,
								draggable: true,
								progress: undefined,
							});
						}
					} else {
						wastedAttemptAudio.play();
						// addStepData(props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.toast.uncovered') + titleCaseConvertedString);
						addStepData("Already Uncovered:"+ titleCaseConvertedString);
						setAttemptsShake(true);
						setTimeout(() => {
							setAttemptsShake(false);
						}, 500);
					}
				})
				.catch((err) => {
					console.log(err);
					// let status = err.response.status;
					// console.log(status);
					// TODO if 401 sign out
					navigate('/oops');
				});

			setCurrentTracker((prev) => ({
				...prev,
				attemptTrack: prev.attemptTrack + 1
			}))
		} // end of if block
		if (currentTracker.attemptTrack === challengeData[0].attempts) {
			setCurrentTracker(prev => ({
				...prev,
				attemptExceed: true
			}))
			errorToast(props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.toast.lastAttempt'));
		}
	};

	const handleComplete = () => {
		setChallengeCompleted(true);
		const uncoveredTests = Object.values(currentTracker.testUncover);
		setUserChallengeData((prevState) => ({
			...prevState,
			numTestUncovered: uncoveredTests.reduce((acc, val) => acc + (val === -1 ? 0 : 1), 0),
		}));
		const formData = {
			response: uncoveredTests,
		};
		const options = {
			method: 'POST',
			url: `/api/user/streams/${stream_id}/challenges/${challenge_id}/end`,
			headers: {
				accept: 'application/json',
			},
			data: formData,
		};
		axios
			.request(options)
			.then(function (response) {
				const challengeEndDetails = response.data.challenge_data[0];
				setUserChallengeData((prevState) => ({
					...prevState,
					challenge_score: challengeEndDetails.challenge_score,
					time_taken: challengeEndDetails.time_taken,
					hints: challengeEndDetails.hints,
				}));
			})
			.catch((err) => {
				let status = err.response.status;
				console.log('error', status);
				navigate('/oops');
			});
	};

	const handleNext = () => {
		const percentage = Math.ceil((currentTracker.currCoverage / challengeData[0].numOfTestCases) * 100);
		if (percentage >= 70) {
			navigate(`/streams/${stream_id}/challenges/${challenge_id}/knowledge/instructions`);
		} else {
			navigate(`/streams/${stream_id}/challenges`);
		}
	};

	const handleHintClick = (hint_display_id, hintNum) => {
		const options = {
			method: 'GET',
			url: `/api/challenges/${challenge_id}/hints/${hintNum}`,
			headers: {
				accept: 'application/json',
			},
		};
		axios
			.request(options)
			.then(function (response) {
				if (hint_display_id === 1) {
					setHintText((prevState) => ({
						...prevState,
						hint1: response.data.data,
					}));
				} else if (hint_display_id === 2) {
					setHintText((prevState) => ({
						...prevState,
						hint2: response.data.data,
					}));
				}
			})
			.catch((err) => {
				let status = err.response.status;
				console.log('error', status);
				navigate('/oops');
			});
	};

	const onboarding = walkthroughJSON.en.onboarding_challenges["steps"]

	const resetWalkthrough = (data) => {
        if (data.status === 'finished' | data.action === 'skip' | data.action === 'tour:end') {
			setStepData([]);
        }
    }

	useEffect(()=>{
		const resourceIncluded = context?.user?.stop_walkthrough?.includes('challenges');
		if(resourceIncluded){
			setStepData([]);
            
		}
	}, [ context?.user?.stop_walkthrough ])
	

	return (
		<>
			<ToastContainer
				id="warning-toast"
				position="top-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop
				closeOnClick
				rtl={false}
				pauseOnFocusLoss
				draggable
				pauseOnHover
			/>
			{loading ? (
				<div>
					<LoadingBackdrop isOpen={true} />
				</div>
			) : challengeData.length > 0 ? (
				<>
					<ChallengeExplanation
						showInfo={showInfo}
						hideChallengeInfo={hideChallengeInfo}
						explanation={challengeData[0].explanation}
						gif_link={challengeData[0].gif_link}
					/>
					{showWalk === 1 && 
						<Joyride
								open={walkthroughJSON.open}
								run={walkthroughJSON.run}
								steps={onboarding}
								resource="challenges"
								disableOverlay={false}
								callback={resetWalkthrough}
							/>
					}
					<ConfirmationCard
						title={props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.info.title')}
						body={props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.info.message')}
						showCard={showCompleteConfirmCard}
						setShowCard={setShowCompleteConfirmCard}
						handleConfirm={handleComplete}
					/>
					<div className={Styles.CardContainer} id="ancestorContainer">
						<div id="infoContainer" style={{ flex: 1 }}>
							<Box
								sx={{
									display: 'flex',
									width: '100%',
									border: (theme) => `1px solid ${theme.palette.info}`,
									borderRadius: 1,
									bgcolor: 'transparent',
									color: 'text.secondary',
									'& svg': { m: 1.5 },
									'& hr': { mx: 0.5 },
									'& > :not(style)': { m: 1 },
								}}
							>
								<ChallengeInfo
									numTestUncovered={userChallengeData.numTestUncovered}
									numOfTestCases={challengeData[0].numOfTestCases}
									currentAttempts={currentTracker.attemptTrack - 1}
									attempts={challengeData[0].attempts}
									minPercent={challengeData[0].minPercent}
									progressPercent={Math.ceil((currentTracker.currCoverage / challengeData[0].numOfTestCases) * 100)}
									attemptsShake={attemptsShake}
								/>
							</Box>
						</div>
						<div
							id="challengeContainer"
							style={{
								display: 'flex',
								flexDirection: 'row',
								gap: '16px',
							}}
						>
							{/* TODO */}
							<div id="interactiveContainer" style={{ width: '80%' }}>
								<Tooltip title={props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.tooltip')} arrow placement="right">
									<div onClick={() => showChallengeInfo()}>
										<p id="challengeName" className={Styles.homeHeader}>
											<InfoOutlinedIcon
												fontSize="small"
												sx={{
													opacity: 0.5,
													mr: '1px',
												}}
											/>
											{challengeData[0].name}
										</p>
										<p id="challengeDescr" className={Styles.description}>
											{challengeData[0].descr}
										</p>
									</div>
								</Tooltip>
								<div id="inputBox-Container" style={{ display: 'flex', flexDirection: 'row', gap: '16px' }}>
									{challengeData[0].input_details.map((value, index) => (
										<div key={value.label} style={{ width: '100%' }}>
											<br></br>
											<Tooltip arrow placement="top" title={value.label}>
												<TextField
													id={`input-${index}`}
													label={
														value.descr.toLowerCase().includes('comma separated value')
															? props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.textLabel')
															: value.label
													}
													variant="filled"
													style={{ width: '100%' }}
													size="small"
													// value={""}
													onChange={(e) => handleInputChange(e, index)}
													inputProps={{ maxLength: 25 }}
												/>
											</Tooltip>
											<FormHelperText id="component-helper-text">{value.descr}</FormHelperText>
										</div>
									))}
								</div>
								<br />
								<div
									id="buttonsContainer"
									style={{
										display: 'flex',
										flexDirection: 'row',
										gap: '24px',
										alignItems: 'center',
										justifyContent: 'center',
									}}
								>
									<button
										id="validate"
										type="submit"
										className={Styles.validateButton}
										disabled={currentTracker.attemptExceed || repeatedInput || challengeCompleted}
										color={currentTracker.attemptExceed ? 'gray' : 'blue'}
										onClick={() => handleValidate(inputs)}
										title={!repeatedInput ? '' : props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.button.validateTitle')}
									>
										{props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.button.validate')}
									</button>
									<button
										id='complete'
										type="submit"
										className={Styles.completeButton}
										color={currentTracker.attemptExceed ? 'gray' : 'blue'}
										disabled={challengeCompleted}
										onClick={() => {
											setShowCompleteConfirmCard(true);
										}}
									>
										{props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.button.complete')}
									</button>
									<button
										id='next'
										className={Styles.nextButton}
										onClick={() => handleNext()}
										color={currentTracker.attemptExceed ? 'gray' : 'blue'}
										disabled={!challengeCompleted}
										title={!challengeCompleted ? props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.button.nextTitle') : ''}
									>
										{props.t('Streams.ChallengeLevelOne.ChallengeLevelOne.button.next')}
									</button>
								</div>
								<br />
								<div
									id="editorContainer"
									style={{
										flex: 1,
										height: '238px',
										display: 'flex',
										flexDirection: 'column',
									}}
								>
									<div
										id="codeReveal"
										className={Styles.outputWindow}
										style={{
											display: focusOnCodeReveal ? 'None' : '',
										}}
									>
										{codeReveal.codeRevealText}
									</div>
									<Paper
										elevation={12}
										sx={{
											display: focusOnCodeReveal ? 'None' : 'flex',
											marginTop: '8px',
											width: '100%',
											height: '80px',
											overflowY: 'auto',
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<Typography sx={{ p: 2, wordWrap: 'break-word' }}>{codeReveal.codeRevealMessage}</Typography>
									</Paper>
									{focusOnCodeReveal && (
										<Backdrop
											sx={{
												color: '#fff',
												zIndex: (theme) => theme.zIndex.drawer + 1,
											}}
											open={focusOnCodeReveal}
											onClick={unfocusOnCodeReveal}
										>
											<div
												id="codeRevealHighlighted"
												className={Styles.highlightedEditor}
												ref={(element) => {
													if (element) {
														element.scrollIntoView({ behavior: 'smooth', block: 'end' });
													}
												}}
											>
												{codeReveal.codeRevealText}
											</div>
											<Paper
												sx={{
													position: 'absolute',
													bottom: '42px',
													left: '45px',
													width: '670px',
													height: '80px',
													overflowY: 'auto',
													display: 'flex',
													justifyContent: 'center',
													alignItems: 'center',
													boxShadow: '0px 2px 6px rgba(255, 255, 255, 0.5)',
												}}
											>
												<Typography sx={{ p: 2, wordWrap: 'break-word' }}>{codeReveal.codeRevealMessage}</Typography>
											</Paper>
										</Backdrop>
									)}
								</div>
							</div>
							{/* end of interactive container */}
							<div
								id="feedback-container"
								style={{
									width: '50%',
									padding: '8px',
									borderRadius: '10px',
									maxHeight: '500px',
									display: 'flex',
									flexDirection: 'column',
									height: '100%',
								}}
							>
									{stepData.length > 0 && stepData[0][0].trim() !== '' && !challengeCompleted && (
										<div
											id="timelineContainer"
											style={{
												height: '100%',
												width: '100%',
												overflowX: 'hidden',
												overflowY: 'auto',
												position: 'relative',
												justifyContent: 'center',
												display: 'flex',
												alignContent: 'center',
											}}
										>
										<TestCaseTimeline attempts={challengeData[0].attempts} stepData={stepData} />
									</div>
								)}
								{challengeCompleted && (
									<div
										style={{
											height: '100%',
											width: '100%',
											// overflow: 'auto',
											position: 'relative',
											display: 'flex',
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<CompleteCard
											numTestUncovered={userChallengeData.numTestUncovered}
											totalTestCases={challengeData[0].numOfTestCases}
											score={userChallengeData.challenge_score}
											percentage={Math.ceil((currentTracker.currCoverage / challengeData[0].numOfTestCases) * 100)}
											timeTaken={userChallengeData.time_taken}
											hints={userChallengeData.hints}
										/>
									</div>
								)}
							</div>
							{/* end of feedback container */}
						</div>
						{/* end of challenge container */}
						{challengeCompleted ? null : (
							<>
								<div
									id="infoFloatContainer"
									style={{
										display: 'flex',
										flexDirection: 'row',
										justifyContent: 'space-between',
										width: 'fit-content',
										height: 'fit-content',
										position: 'fixed',
										bottom: 0,
										right: 0,
										paddingRight: '9.4em',
										paddingBottom: '30px',
									}}
								>
									<ChallengeInfoFloat
										numTestUncovered={userChallengeData.numTestUncovered}
										numOfTestCases={challengeData[0].numOfTestCases}
										currentAttempts={currentTracker.attemptTrack - 1}
										attempts={challengeData[0].attempts}
										progressPercent={Math.ceil((currentTracker.currCoverage / challengeData[0].numOfTestCases) * 100)}
									/>
								</div>
								<div id="hintCompContainer" className={Styles.hintContainer}>
									<Hints hint1Text={hintText.hint1} hint2Text={hintText.hint2} handleHintClick={handleHintClick} />
								</div>
							</>
						)}
					</div>
				</>
			) : (
				// end of parent container with both challenge info and challenge
				<div>
					<LoadingBackdrop isOpen={true} />
				</div>
			)}
		</>
	);
}
